import { makeAutoObservable, reaction } from 'mobx'
import { type IDropdownItem } from 'shared/ui'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type MessageFieldStore } from 'widgets/MessageField'
import { mentionReplacer } from 'widgets/MessageField/ui/DropdownMentions/lib/mentionReplacer'

type IMentionsStoreData = {
  origin: string
  search: string
  startOffset: number
  lastOffset: number
}

export class DropdownMentionsStore {
  private _loading = false
  private _search = ''
  private _itemsMap: Map<number | string, IDropdownItem> = new Map()
  private _itemInbox: IDropdownItem = {
    id: 'inbox',
    label: '@inbox',
    labelRight: 'Notify everyone in this inbox',
    avatarProps: {
      info: {
        icon: 'announcement',
        color: 'transparent',
      },
      size: 16,
    },
  }
  private _show = false
  private _data: IMentionsStoreData[] = []
  private _selectedData: IMentionsStoreData | null = null
  private _range: Range | null = null
  private _disposeUsers: ReturnType<typeof reaction> | null = null

  constructor(private _messageFieldStore: MessageFieldStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  disposeReactions = () => {
    this._disposeUsers?.()
  }

  dispose = () => {
    this._show = false
    this._data = []
    this._selectedData = null
    this._range = null
  }

  get conversationStore() {
    return this._entities.conversationStore
  }

  handleInit = async () => {
    this._itemsMap.clear()

    const conversation = this.conversationStore?.currentItem

    if (!conversation || !conversation.inbox_id) {
      this._entities.usersStore.users.forEach((user) => {
        const item: IDropdownItem = {
          id: user.id,
          label: user.name,
          avatarProps: {
            info: user.avatarInfo,
            size: 16,
          },
          ariaLabel: 'tagsMembersListSelector',
        }
        this.addItem(item)
      })
      return
    }

    const inbox = await this._entities.inboxesStore.getById(conversation.inbox_id)

    this._entities.usersStore.users.forEach((user) => {
      const existMember = inbox?.type === 'inbox' && inbox.memberIds.includes(user.id)

      const item: IDropdownItem = {
        id: user.id,
        label: user.name,
        avatarProps: {
          info: user.avatarInfo,
          size: 16,
        },
        ariaLabel: 'tagsMembersListSelector',
        labelRight: !existMember ? 'Not in inbox' : '',
      }

      this.addItem(item)
    })
  }

  handleSearch = (value: string) => {
    this._search = value
  }

  addItem = (item: IDropdownItem) => {
    this._itemsMap.set(item.id, item)
  }

  get loading() {
    return this._loading
  }

  get itemInbox() {
    return this._itemInbox
  }

  get search() {
    return this._search
  }

  get show() {
    return this._show
  }

  get items() {
    if (this._search) {
      return Array.from(this._itemsMap.values()).filter((item) => {
        return item.label.toUpperCase().includes(this._search.toUpperCase())
      })
    }

    return Array.from(this._itemsMap.values())
  }

  get itemsSimple() {
    return this.items.filter((item) => !item.labelRight)
  }

  get itemsNotInbox() {
    return this.items.filter((item) => item.labelRight)
  }

  private getEditableText = (element: HTMLElement) => {
    const clone = element.cloneNode(true)
    if (!(clone instanceof HTMLElement)) return ''

    clone.querySelectorAll('[data-readonly]').forEach((node) => node.remove())
    return clone.innerText
  }

  private isInsideReadonly = (node: Node): boolean => {
    const element = node instanceof HTMLElement ? node : node.parentElement
    return Boolean(element?.closest('[data-readonly]'))
  }

  checkMessageKey = (range: Range | null) => {
    if (!range) return

    if (this.isInsideReadonly(range.endContainer)) {
      this._range = range
      this._search = ''
      this._show = false
      return
    }

    this._range = range

    const messageText =
      range.endContainer instanceof Text
        ? range.endContainer?.data
        : range.endContainer instanceof HTMLElement
        ? this.getEditableText(range.endContainer)
        : ''

    const items = [...messageText.matchAll(/@([a-zA-Z0-9_]*)/g)]

    const startOffset =
      range.endContainer instanceof Text
        ? range.startOffset
        : range.endContainer instanceof HTMLElement
        ? this.getEditableText(range.endContainer).indexOf('@') + 1
        : 0

    this._data = items.map((item) => {
      const index = item.index || 0

      return {
        origin: item[0],
        search: item[1],
        startOffset: index,
        lastOffset: item[0].length + index,
      }
    })
    this._selectedData =
      this._data.find(
        (item) => startOffset >= item.startOffset && startOffset <= item.lastOffset
      ) || null

    if (this._selectedData) {
      const searchOrigin = this._selectedData.origin.slice(
        0,
        startOffset - this._selectedData.startOffset
      )

      this._search = searchOrigin.replace('@', '')
      this._show = Boolean(searchOrigin.length)
    } else {
      this._search = ''
      this._show = false
    }
  }

  handleSetMentions = (item: IDropdownItem) => {
    if (!this._messageFieldStore.replaceContent) return
    if (!this._range) return
    if (!this._selectedData) return

    if (!this._messageFieldStore.isModeNote && !this._messageFieldStore.hasMention()) {
      this._messageFieldStore.setNoteMode(true)
    }

    const startOffset =
      this._range.endContainer instanceof Text
        ? this._range.startOffset
        : this._range.endContainer instanceof HTMLElement
        ? this.getEditableText(this._range.endContainer).indexOf('@') + 1
        : 0

    const nodeData =
      this._range.endContainer instanceof Text
        ? this._range.endContainer?.data
        : this._range.endContainer instanceof HTMLElement
        ? this.getEditableText(this._range.endContainer)
        : ''

    if (nodeData && this._selectedData.startOffset < startOffset) {
      const replaceString = (s: string) => {
        return s.replace(`@${this._search}`, `${mentionReplacer(item)} &nbsp;`)
      }

      const messageText = replaceString(nodeData)

      this._messageFieldStore.replaceContent(
        messageText,
        this._range.endContainer instanceof Text ? this._range.endContainer : undefined
      )
      this.dispose()
    }
  }

  reactionUsers = () => {
    this._disposeUsers?.()
    this._disposeUsers = reaction(
      () => [this._entities.usersStore.users.length, this.conversationStore.currentItem],
      () => {
        this.handleInit()
      },
      {
        fireImmediately: true,
      }
    )
  }
}
